import React, { useState } from 'react';
import styled from 'styled-components';

const ContactContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-family: 'Lucida Bright', 'Lucida Serif', Georgia, serif;
  font-size: 1.65rem;
  margin: 0 0 15px 0;
  color: #333;
`;

const Description = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 20px;
  color: #444;
`;

const ContactForm = styled.form`
  margin-top: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  border-radius: 3px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #666;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  min-height: 150px;
  border-radius: 3px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #666;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
  }
`;

const SubmitButton = styled.button`
  background-color: #333;
  color: white;
  border: none;
  padding: 10px 20px;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  cursor: pointer;
  border-radius: 3px;
  transition: background-color 0.2s;
  
  &:hover:not(:disabled) {
    background-color: #555;
  }

  &:disabled {
    background-color: #999;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.p<{ isError?: boolean }>`
  margin-top: 15px;
  padding: 10px;
  border-radius: 3px;
  text-align: center;
  font-size: 0.85rem;
  font-family: 'Inter', sans-serif;
  color: ${props => props.isError ? '#D8000C' : '#4F8A10'};
  background-color: ${props => props.isError ? '#FFD2D2' : '#DFF2BF'};
`;

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [isError, setIsError] = useState(false);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatusMessage('');
    setIsError(false);

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setStatusMessage('Thank you! Your message has been sent successfully.');
    setFormData({ name: '', email: '', message: '' });
      } else {
        setIsError(true);
        setStatusMessage(result.error || 'An error occurred while sending your message. Please try again later.');
        console.error('Submission Error:', result);
      }
    } catch (error) {
      console.error('Fetch Error:', error);
      setIsError(true);
      setStatusMessage('Could not connect to the server. Please check your internet connection or try again later.');
    }

    setIsSubmitting(false);
  };
  
  return (
    <ContactContainer>
      <PageTitle>Contact</PageTitle>
      <Description>
        If you're interested in collaborating, commissioning a piece, 
        or simply want to get in touch, please use the form below.
      </Description>
      
      <ContactForm onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="name">Name</Label>
          <Input 
            type="text" 
            id="name" 
            name="name" 
            value={formData.name} 
            onChange={handleChange} 
            required 
            disabled={isSubmitting}
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="email">Email</Label>
          <Input 
            type="email" 
            id="email" 
            name="email" 
            value={formData.email} 
            onChange={handleChange} 
            required 
            disabled={isSubmitting}
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="message">Message</Label>
          <Textarea 
            id="message" 
            name="message" 
            value={formData.message} 
            onChange={handleChange} 
            required 
            disabled={isSubmitting}
          />
        </FormGroup>
        
        <SubmitButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </SubmitButton>

        {statusMessage && (
          <StatusMessage isError={isError}>
            {statusMessage}
          </StatusMessage>
        )}
      </ContactForm>
    </ContactContainer>
  );
};

export default Contact; 